import { useCallback, useId, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';

import { bold, InlineText, TimelineEvent } from '../components/shared';
import { TIMELINE_DESIGN_TOKENS } from '../design-tokens';
import type { WebhookOutcomePayload } from '../types';
import type { EventComponentProps } from './types';
import { webhookOutcomeReason } from './webhookOutcomeCopy';

export function WebhookAttemptFailedEvent({ event }: EventComponentProps) {
  const payload = event.payload as WebhookOutcomePayload | undefined;
  const reason = webhookOutcomeReason(payload?.last_error);
  const attempt = payload?.attempt ?? payload?.attempt_count;

  const snippetId = useId();
  const [showSnippet, setShowSnippet] = useState(false);
  const toggleSnippet = useCallback(() => setShowSnippet(prev => !prev), []);

  const snippet = payload?.last_response_snippet?.trim();

  const snippetMarkdown = useMemo(() => {
    if (!snippet) {
      return undefined;
    }
    return `\`\`\`\n${snippet}\n\`\`\``;
  }, [snippet]);

  const headerLabel = Number.isFinite(attempt)
    ? `Webhook attempt ${attempt} failed`
    : 'Webhook attempt failed';

  const ariaLabel = reason ? `${headerLabel} – ${reason}` : headerLabel;

  const lastStatus = payload?.last_status;
  const metaText =
    lastStatus != null && Number.isFinite(lastStatus)
      ? `Retrying… · HTTP ${lastStatus}`
      : 'Retrying…';

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <InlineText>
            {bold(headerLabel)}
            {reason ? <> – {reason}</> : null}
          </InlineText>
        }
        timestamp={event.occurred_at}
      >
        <div
          className={`${TIMELINE_DESIGN_TOKENS.typography.subItem} text-fg-secondary`}
        >
          {metaText}
        </div>
        {snippetMarkdown && (
          <div id={snippetId} className='mt-2'>
            <Button
              color='link-gray'
              size='sm'
              onClick={toggleSnippet}
              aria-expanded={showSnippet}
              aria-controls={snippetId}
              className='!font-normal'
            >
              {showSnippet ? 'Hide response' : 'Show response'}
            </Button>
            {showSnippet && (
              <MarkdownContent
                content={snippetMarkdown}
                size='compact'
                className='mt-2'
              />
            )}
          </div>
        )}
      </TimelineEvent.Content>
    </TimelineEvent>
  );
}
